/**
 * Stock Chart Viewer - API Usage Tracking
 * 
 * Uses backend API for accurate cross-device tracking.
 * Falls back to localStorage if backend is unavailable.
 */

import { elements } from '../ui/dom.js';
import { API_KEY } from '../config.js';

// Backend API URL from Vite env (空文字列の場合は相対パスを使用)
const API_URL = import.meta.env.VITE_API_URL ?? '';
// Safe hardcoded limit fallback if env not parsed correctly
// API Daily Limit (Default fallback)
export const API_DAILY_LIMIT_DEFAULT = 300;

// Local fallback storage key
const API_USAGE_KEY = 'apiUsageTracker';

// Cache current usage state
let currentUsage = {
    used: 0,
    remaining: API_DAILY_LIMIT_DEFAULT,
    limit: API_DAILY_LIMIT_DEFAULT,
    resetAt: null,
};

/**
 * Fetch current usage from backend API.
 */
export async function fetchApiUsage() {
    try {
        const response = await fetch(`${API_URL}/api/usage?api_key=${encodeURIComponent(API_KEY)}`);
        if (response.ok) {
            const data = await response.json();
            currentUsage = {
                used: data.used,
                remaining: data.remaining,
                limit: data.limit,
                resetAt: data.reset_at,
            };
            updateApiUsageUI();
            return currentUsage;
        }
    } catch (error) {
        console.warn('Backend API unavailable, using localStorage fallback:', error.message);
    }

    // Fallback to localStorage
    return getLocalUsage();
}

/**
 * Increment usage via backend API before making Alpha Vantage call.
 */
export async function incrementApiUsage() {
    try {
        const response = await fetch(`${API_URL}/api/usage/increment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ api_key: API_KEY }),
        });

        if (response.ok) {
            const data = await response.json();
            currentUsage.used = data.used;
            currentUsage.remaining = data.remaining;
            updateApiUsageUI();

            if (data.limit_reached) {
                console.warn('API daily limit reached!');
            }

            return { success: data.success, limitReached: data.limit_reached };
        }
    } catch (error) {
        console.warn('Backend API unavailable, using localStorage fallback:', error.message);
    }

    // Fallback to localStorage
    return incrementLocalUsage();
}

/**
 * Check if API limit is reached.
 */
export function isLimitReached() {
    return currentUsage.remaining <= 0;
}

/**
 * Get current usage state.
 */
export function getApiUsage() {
    return currentUsage;
}

// ============================================
// localStorage Fallback Functions
// ============================================

function getLocalUsage() {
    const stored = localStorage.getItem(API_USAGE_KEY);
    if (stored) {
        try {
            const usage = JSON.parse(stored);
            const lastReset = new Date(usage.lastReset);
            const now = new Date();
            const hoursSinceReset = (now - lastReset) / (1000 * 60 * 60);

            if (hoursSinceReset >= 24) {
                return resetLocalUsage();
            }

            currentUsage.used = usage.count;
            currentUsage.remaining = Math.max(0, API_DAILY_LIMIT_DEFAULT - usage.count);
            updateApiUsageUI();
            return currentUsage;
        } catch (e) {
            return resetLocalUsage();
        }
    }
    return resetLocalUsage();
}

function resetLocalUsage() {
    const usage = {
        count: 0,
        lastReset: new Date().toISOString(),
    };
    localStorage.setItem(API_USAGE_KEY, JSON.stringify(usage));
    currentUsage.used = 0;
    currentUsage.remaining = API_DAILY_LIMIT_DEFAULT;
    return currentUsage;
}

function incrementLocalUsage() {
    const stored = localStorage.getItem(API_USAGE_KEY);
    let usage = stored ? JSON.parse(stored) : { count: 0, lastReset: new Date().toISOString() };
    usage.count++;
    localStorage.setItem(API_USAGE_KEY, JSON.stringify(usage));

    currentUsage.used = usage.count;
    currentUsage.remaining = Math.max(0, API_DAILY_LIMIT_DEFAULT - usage.count);
    updateApiUsageUI();

    return { success: true, limitReached: currentUsage.remaining <= 0 };
}

// ============================================
// UI Update Functions
// ============================================

export function updateApiUsageUI() {
    const used = currentUsage.used;
    const remaining = currentUsage.remaining;
    const limit = currentUsage.limit || API_DAILY_LIMIT_DEFAULT;
    const percentage = Math.min(100, (used / limit) * 100);

    if (elements.apiUsedCount) elements.apiUsedCount.textContent = used;
    if (elements.apiRemainingCount) elements.apiRemainingCount.textContent = remaining;
    if (elements.apiUsageFill) {
        elements.apiUsageFill.style.width = `${percentage}%`;
        elements.apiUsageFill.classList.remove('warning', 'danger');
        if (percentage >= 80) {
            elements.apiUsageFill.classList.add('danger');
        } else if (percentage >= 50) {
            elements.apiUsageFill.classList.add('warning');
        }
    }

    // Reset time
    if (elements.apiResetTime && currentUsage.resetAt) {
        const resetTime = new Date(currentUsage.resetAt);
        const hoursLeft = Math.max(0, Math.ceil((resetTime - new Date()) / (1000 * 60 * 60)));
        elements.apiResetTime.textContent = `リセットまで: 約${hoursLeft}時間`;
    }

    // Update header status
    if (elements.headerApiCount) {
        elements.headerApiCount.textContent = `${used}/${limit}`;
    }
}
